import { useState, type FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import postalCodeRegex from '@eneco/common/validation/postalCodeRegex';
import PostalCodeInput from '@eneco/components/PostalCodeInput/PostalCodeInput';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { useProductsPublicGetInterruptions } from '@eneco/dc/hooks';
import { Box, Button, Form, Stack, TextLink } from '@eneco/sparky';
import type { ResponseModels_Products_Interruption_InterruptionModel } from '@monorepo-types/dc';

import { WARMTH_INTERRUPTIONS_SLOTS, resolveSlot } from './slots';
import type { WarmthInterruptionsContent } from './warmth-interruptions-content.types';
import { WarmthInterruptionsDialog } from './WarmthInterruptionsDialog';

/**
 * `WarmthInterruptions` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/WarmthInterruptions/WarmthInterruptions.tsx`: an intro
 * slot, a postal-code field that opens the interruptions near that postal code, and a link to the full
 * list, both from the public interruptions endpoint. Drops the Experience Editor `wrap()` calls, which
 * outside the editor return the plain field values.
 */
const WarmthInterruptions: FC = () => {
  const { adapter } = useCms();
  const content = useContent<WarmthInterruptionsContent>();
  const introSlot = resolveSlot(useRendering(), WARMTH_INTERRUPTIONS_SLOTS.intro);

  const { data } = useProductsPublicGetInterruptions();
  const [filteredData, setFilteredData] =
    useState<ResponseModels_Products_Interruption_InterruptionModel['data']>(null);
  const [postalCode, setPostalCode] = useState<string>();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string>();
  const { postalCodeFormField } = content;

  const filterData = () => {
    if (!!postalCode && postalCodeRegex.test(postalCode)) {
      setFilteredData(data?.filter(item => item.address?.postalCode === postalCode));
      setError(undefined);
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
      setError(postalCodeFormField?.validationMessage);
    }
  };

  return (
    <Box backgroundColor="backgroundSecondary" borderRadius="m" padding="6">
      <Stack gap="6">
        {introSlot ? <Placeholder name={introSlot} /> : null}
        <Form
          onSubmit={event => {
            event.preventDefault();
            filterData();
          }}>
          <PostalCodeInput
            name="postal-code"
            label={postalCodeFormField?.label ?? ''}
            hint={postalCodeFormField?.hint}
            placeholder={postalCodeFormField?.placeholder}
            value={postalCode}
            onChange={setPostalCode as (...event: unknown[]) => void}
            error={error}
          />
        </Form>
        <Stack.Item shrink>
          <Button onClick={filterData}>{content.submitButtonText}</Button>
          <TrackedDialog isOpen={isDialogOpen} setOpen={setDialogOpen} title={content.dialogInterruptionsNearByTitle}>
            <WarmthInterruptionsDialog interruptions={filteredData} content={content} toRichText={adapter.toRichText} />
          </TrackedDialog>
        </Stack.Item>
        <Stack.Item shrink>
          <TrackedDialog
            title={content.dialogInterruptionsTitle}
            trigger={<TextLink emphasis="high">{content.viewAllInterruptionsLinkText}</TextLink>}>
            <WarmthInterruptionsDialog interruptions={data} content={content} toRichText={adapter.toRichText} />
          </TrackedDialog>
        </Stack.Item>
      </Stack>
    </Box>
  );
};

export default WarmthInterruptions;
